import { useEffect, useState } from "react";
import { useAuth } from "../../hooks/useAuth";
import { getAssignedPromoters, getTarget, setTarget } from "../../firebase/services";
import { format } from "date-fns";
import { useLanguage } from "../../hooks/useLanguage";

export default function SupervisorTargets() {
  const { t } = useLanguage();
  const { user, profile } = useAuth();
  const [month, setMonth] = useState(format(new Date(), "yyyy-MM"));
  const [promoters, setPromoters] = useState([]);
  const [targets, setTargets] = useState({});
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ sellTarget: "", highendTarget: "" });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");

  const load = async () => {
    if (!user) return;
    const proms = await getAssignedPromoters(user.uid);
    setPromoters(proms);
    const tm = {};
    await Promise.all(proms.map(async p => {
      const t = await getTarget(p.uid, month, true); // gồm cả pending để hiện trạng thái
      if (t) tm[p.uid] = t;
    }));
    setTargets(tm);
  };

  useEffect(() => { load(); }, [user, month]);

  const startEdit = (p) => {
    setEditing(p.uid);
    const t = targets[p.uid];
    setForm({ sellTarget: t?.sellTarget || "", highendTarget: t?.highendTarget || "" });
  };

  const handleSave = async (p) => {
    setSaving(true);
    try {
      await setTarget({
        promoterId: p.uid, month,
        sellTarget: Number(form.sellTarget),
        highendTarget: Number(form.highendTarget),
        actorName: profile?.name || "Supervisor",
        actorRole: "supervisor",
        actorId: user.uid,
        promoterName: p.name,
      });
      setMsg(`Đã gửi đề xuất target cho ${p.name} — chờ admin duyệt`);
      setEditing(null);
      load();
    } catch (err) {
      setMsg("Lỗi: " + err.message);
    } finally {
      setSaving(false);
      setTimeout(() => setMsg(""), 4000);
    }
  };

  // Trạng thái + diff đề xuất → duyệt
  const renderStatus = (tgt) => {
    if (!tgt) return null;
    if (tgt.status === "pending") return <span className="badge badge-amber">{t("tga.wait")}</span>;
    // approved (hoặc target cũ không có status)
    const adjusted = tgt.proposedSell != null
      && (tgt.proposedSell !== tgt.sellTarget || tgt.proposedHighend !== tgt.highendTarget);
    return (
      <>
        <span className="badge badge-green">{t("tga.approved")}</span>
        {adjusted && (
          <div style={{ fontSize: 11, color: "#B45309", marginTop: 4 }}>
            {t("tga.adj_note")}: DS {tgt.proposedSell} → <strong>{tgt.sellTarget}</strong>, HE {tgt.proposedHighend} → <strong>{tgt.highendTarget}</strong>
          </div>
        )}
      </>
    );
  };

  return (
    <>
      <div className="page-header">
        <div><h1>{t("target.title_sv")}</h1><p>{t("tga.sv_subtitle")}</p></div>
        <input type="month" value={month} onChange={e => setMonth(e.target.value)} style={{ width: 160 }} />
      </div>

      {msg && <div className={`alert ${msg.startsWith("Lỗi") ? "alert-error" : "alert-success"}`}>{msg}</div>}

      <div className="card">
        <table className="table-card">
          <thead><tr><th>{t("sales.promoter")}</th><th>{t("sales.shop")}</th><th>{t("target.sell_target")}</th><th>{t("target.he_target")}</th><th>{t("x.status")}</th><th></th></tr></thead>
          <tbody>
            {promoters.map(p => {
              const tgt = targets[p.uid];
              const locked = tgt && tgt.status !== "pending"; // đã duyệt (hoặc target cũ) → khóa
              return (
                <tr key={p.uid}>
                  <td data-label="Promoter"><strong>{p.name}</strong></td>
                  <td data-label="Shop">{p.shopName}</td>
                  <td data-label="Target DS">
                    {editing === p.uid
                      ? <input type="number" value={form.sellTarget} onChange={e => setForm(f => ({ ...f, sellTarget: e.target.value }))} style={{ width: 80 }} />
                      : tgt?.sellTarget ?? <span style={{ color: "#aaa" }}>{t("target.not_set")}</span>}
                  </td>
                  <td data-label="Target HE">
                    {editing === p.uid
                      ? <input type="number" value={form.highendTarget} onChange={e => setForm(f => ({ ...f, highendTarget: e.target.value }))} style={{ width: 80 }} />
                      : tgt?.highendTarget ?? <span style={{ color: "#aaa" }}>{t("target.not_set")}</span>}
                  </td>
                  <td data-label="Status">{renderStatus(tgt)}</td>
                  <td data-label="">
                    {editing === p.uid ? (
                      <div style={{ display: "flex", gap: 6 }}>
                        <button className="btn btn-primary btn-sm" onClick={() => handleSave(p)} disabled={saving}>{saving ? t("common.saving") : t("tga.send")}</button>
                        <button className="btn btn-sm" onClick={() => setEditing(null)}>{t("common.cancel")}</button>
                      </div>
                    ) : locked ? (
                      <span style={{ fontSize: 11, color: "#aaa" }}>{t("tga.locked")}</span>
                    ) : (
                      <button className="btn btn-sm" onClick={() => startEdit(p)}>{tgt ? t("common.edit") : t("tga.propose")}</button>
                    )}
                  </td>
                </tr>
              );
            })}
            {promoters.length === 0 && <tr><td colSpan={6} style={{ color: "#aaa", textAlign: "center", padding: 24 }}>{t("sv_dash.no_promoters")}</td></tr>}
          </tbody>
        </table>
      </div>
    </>
  );
}
